import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, MessageSquareText, UserRoundCog, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTarefasAjuda } from '@/hooks/useTarefas';
import { tipoCartaoTarefa, type TipoCartaoTarefa } from '@/lib/ajudaRespondida';

/**
 * CARTÕES DE TAREFA NO CANTO (Douglas 30/09/2026) — o espelho do "Pediram sua
 * ajuda". Três avisos que eram gravados e a tela descartava por serem de fluxo:
 *  · AJUDA RESPONDIDA — para quem abriu a tarefa, quando o ajudante responde;
 *  · ATENDIMENTO AO DONO — para o dono, o supervisor dele e a gerência;
 *  · TAREFA CONCLUÍDA — para quem abriu o atendimento, quando o dono finaliza.
 * Cartão pequeno, sem popup: fica até a pessoa abrir; o X adia 30 minutos.
 * Abrir confirma o aviso e leva à tarefa.
 */
export interface AvisoRespondida {
  id: string;
  titulo: string;
  mensagem: string;
  metadados?: {
    origem?: string;
    cta_path_confirm?: string;
    empresa?: string | null;
    tarefa_titulo?: string | null;
    quem_respondeu?: string | null;
    resposta?: string | null;
    dono_nome?: string | null;
    aberto_por_nome?: string | null;
    urgente?: boolean | null;
    para_o_dono?: boolean | null;
    para_gestao?: boolean | null;
    tipo_nome?: string | null;
    vence_em?: string | null;
  } | null;
}

const primeiro = (s?: string | null) => String(s || '').split(' ')[0];

const empresaDe = (a: AvisoRespondida) =>
  a.metadados?.empresa || String(a.titulo || '').split(' · ').slice(1).join(' · ') || null;

const tarefaDe = (a: AvisoRespondida) =>
  a.metadados?.tarefa_titulo || String(a.mensagem || '').split('\n')[0] || 'Tarefa';

interface Texto { icone: LucideIcon; titulo: string; linha: string; citacao?: string | null; urgente?: boolean; selo?: string | null }

function textoDe(tipo: TipoCartaoTarefa, avisos: AvisoRespondida[]): Texto {
  const a = avisos[0];
  const m = a.metadados || {};
  const alvo = empresaDe(a) || tarefaDe(a);
  const mais = avisos.length > 1 ? ` e mais ${avisos.length - 1}` : '';
  if (tipo === 'atendimento') {
    return {
      icone: UserRoundCog,
      titulo: avisos.length > 1 ? `Atendimento ao dono · ${avisos.length} clientes`
        : m.para_o_dono ? 'Pediram que você atenda' : `Atendimento aberto para ${primeiro(m.dono_nome)}`,
      linha: `${primeiro(m.aberto_por_nome)} · ${alvo}${mais}`,
      citacao: avisos.length === 1 ? tarefaDe(a) : null,
      urgente: avisos.some((x) => x.metadados?.urgente),
    };
  }
  if (tipo === 'concluida') {
    return {
      icone: CheckCircle2,
      titulo: avisos.length > 1 ? `Tarefa concluída em ${avisos.length} atendimentos` : 'Tarefa concluída',
      linha: avisos.length > 1 ? `${alvo}${mais}` : [primeiro(m.quem_respondeu), alvo].filter(Boolean).join(' · '),
      citacao: avisos.length === 1 ? m.resposta : null,
    };
  }
  return {
    icone: MessageSquareText,
    titulo: avisos.length > 1 ? `Ajuda respondida em ${avisos.length} tarefas` : 'Ajuda respondida',
    linha: avisos.length > 1 ? `${alvo}${mais}` : [primeiro(m.quem_respondeu), alvo].filter(Boolean).join(' · '),
    citacao: avisos.length === 1 ? m.resposta : null,
  };
}

const DESTINO_VARIOS: Record<TipoCartaoTarefa, string> = {
  atendimento: '/tarefas',
  respondida: '/whatsapp-inbox?tarefas=1',
  concluida: '/tarefas',
};

const ORDEM: TipoCartaoTarefa[] = ['atendimento', 'respondida', 'concluida'];

export default function AvisoAjudaRespondida({ avisos, onConfirmar }: {
  avisos: AvisoRespondida[];
  onConfirmar: (id: string) => Promise<void>;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [adiado, setAdiado] = useState<Partial<Record<TipoCartaoTarefa, number>>>({});
  const { data: convites = [] } = useTarefasAjuda('pendente', avisos.length > 0);

  const agora = Date.now();
  const grupos = ORDEM
    .map((tipo) => ({ tipo, lista: avisos.filter((a) => tipoCartaoTarefa(a) === tipo) }))
    .filter((g) => g.lista.length && agora >= (adiado[g.tipo] ?? 0));
  if (!grupos.length) return null;
  const outroCartao = convites.length > 0 && !pathname.startsWith('/tarefas');

  const abrir = async (tipo: TipoCartaoTarefa, lista: AvisoRespondida[]) => {
    const destino = lista.length === 1
      ? lista[0].metadados?.cta_path_confirm || DESTINO_VARIOS[tipo]
      : lista.every((a) => a.metadados?.para_gestao) ? '/tarefas' : DESTINO_VARIOS[tipo];
    await Promise.all(lista.map((a) => onConfirmar(a.id).catch(() => undefined)));
    navigate(destino);
  };

  return (
    <div className={`fixed right-6 z-50 flex w-[340px] flex-col-reverse gap-2 ${outroCartao ? 'bottom-[164px]' : 'bottom-24'}`}>
      {grupos.map(({ tipo, lista }) => {
        const tx = textoDe(tipo, lista);
        const Icone = tx.icone;
        return (
          <div key={tipo} role="status" className="flex items-center gap-3 rounded-xl border border-border-warm bg-card px-3 py-2.5 shadow-lg [box-shadow:inset_3px_0_0_hsl(var(--primary)),0_10px_24px_-12px_rgba(0,0,0,.35)]">
            <Icone className="h-[18px] w-[18px] flex-shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
                <span className="truncate">{tx.titulo}</span>
                {tx.selo && <span className="flex-shrink-0 rounded bg-primary/10 px-1.5 text-[9px] font-bold uppercase tracking-[0.06em] text-primary">{tx.selo}</span>}
                {tx.urgente && <span className="flex-shrink-0 rounded bg-primary/10 px-1.5 text-[9px] font-bold uppercase tracking-[0.06em] text-primary">Urgente</span>}
              </p>
              <p className="truncate text-[10.5px] text-muted-foreground">{tx.linha}</p>
              {tx.citacao && <p className="truncate text-[10.5px] italic text-muted-foreground/80">“{tx.citacao}”</p>}
            </div>
            <button type="button" onClick={() => abrir(tipo, lista)}
              className="h-[27px] flex-shrink-0 whitespace-nowrap rounded-lg bg-primary px-2.5 text-[11px] font-semibold text-white hover:brightness-110">
              {lista.length === 1 ? 'Acessar tarefa' : 'Abrir'}
            </button>
            <button type="button" aria-label="Depois" title="Depois (volta em 30 min)"
              onClick={() => setAdiado((a) => ({ ...a, [tipo]: Date.now() + 30 * 60_000 }))}
              className="grid h-[22px] w-[22px] flex-shrink-0 place-items-center rounded text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
